import classnames from "classnames";
import PropTypes from "prop-types";
import { forwardRef } from "react";
import { Link } from "react-router-dom";

import { ALTERNATIVE_LINKS } from "../../constants";
import { checkIfElementsExistInString } from "../../helpers";
import Icon, { iconSizes } from "../Icon";

import "./button.scss";

export const Button = forwardRef(
  (
    {
      children,
      variant,
      squared,
      collapsible,
      href,
      size,
      iconName,
      iconPosition,
      iconSize,
      disabled,
      className,
      active,
      ...restProps
    },
    ref
  ) => {
    const classNamePrefix = "button";
    const classNames = classnames(classNamePrefix, className, {
      [`${classNamePrefix}--${variant}`]: variant,
      [`${classNamePrefix}--squared`]: squared,
      [`${classNamePrefix}--${size}`]: size,
      [`${classNamePrefix}--${iconPosition}`]: iconPosition,
      [`${classNamePrefix}--icon-only`]: iconName && !children,
      [`${classNamePrefix}--collapsible`]: collapsible,
    });

    const mapSizeToIconSize = {
      mini: "small",
      small: "medium",
      medium: "large",
      large: "large",
    };

    const content = (
      <>
        {iconName && (
          <Icon
            name={iconName}
            size={iconSize || mapSizeToIconSize[size]}
            aria-hidden={true}
            className={`${classNamePrefix}__icon`}
          />
        )}
        <span className={`${classNamePrefix}__children`}>{children}</span>
      </>
    );

    if (href) {
      // Use the default anchor tag if href is an alternative link
      if (checkIfElementsExistInString(href, ALTERNATIVE_LINKS)) {
        return (
          <a
            href={href}
            className={classNames}
            active={active && active !== "false" ? "true" : "false"}
            {...restProps}
          >
            {content}
          </a>
        );
      }

      return (
        <Link reloadDocument to={href} className={classNames} {...restProps}>
          {content}
        </Link>
      );
    }

    return (
      <button
        ref={ref}
        className={classNames}
        disabled={disabled}
        aria-label={iconName && !children ? iconName : null}
        {...restProps}
      >
        {content}
      </button>
    );
  }
);

export const buttonVariants = [
  "primary",
  "secondary",
  "outline",
  "link",
  "danger",
];
export const buttonSizes = ["mini", "small", "medium", "large"];
export const buttonIconPositions = ["left", "right"];

Button.propTypes = {
  variant: PropTypes.oneOf(buttonVariants),
  squared: PropTypes.bool,
  collapsible: PropTypes.bool,
  href: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  size: PropTypes.oneOf(buttonSizes),
  iconName: PropTypes.string,
  iconPosition: PropTypes.oneOf(buttonIconPositions),
  iconSize: PropTypes.oneOf(iconSizes),
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

Button.defaultProps = {
  children: undefined,
  variant: "primary",
  squared: false,
  collapsible: false,
  href: undefined,
  size: "small",
  iconName: undefined,
  iconPosition: "left",
  iconSize: undefined,
  disabled: false,
  className: undefined,
};

export default Button;
